import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

import { UtcDatePipe } from '@mds/common';
import { ButtonComponent, ChipComponent, DropdownMenuComponent } from '@mds/ui';
import { PCR, Standard } from '../../utils/epd-dashboard.model';

interface HeaderSelectOption {
  helperText: string;
  options: any[] | null;
  selectedValue: any[];
  text: string;
  change: (value: any[]) => void;
}

@Component({
  selector: 'app-epd-dashboard-header-controls',
  standalone: true,
  imports: [CommonModule, ChipComponent, DropdownMenuComponent, ButtonComponent, UtcDatePipe],
  templateUrl: './epd-dashboard-header-controls.component.html',
  styleUrls: ['./epd-dashboard-header-controls.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EpdDashboardHeaderControlsComponent {
  @Input() selectedPcr: PCR;
  @Input() dataUpdated;
  @Input() selectedMethod: Standard;
  @Input() methodList: Standard[] = [];
  @Input() pcrList: PCR[] = [];
  @Input() languages: any[] | null = null;
  @Input() selectedLanguage;
  @Input() canPreview = false;
  @Input() previewButtonLabel = 'Preview & Export';

  @Output() methodologyChange = new EventEmitter<Standard[]>();
  @Output() pcrChange = new EventEmitter<PCR[]>();
  @Output() languageChange = new EventEmitter<any[]>();
  @Output() preview = new EventEmitter<void>();

  get selectList() {
    return [...this.standardSelectList, this.pcrDropdown, ...this.languageSelectList];
  }

  get standardSelectList() {
    return this.selectedMethod ? [this.standardDropdown] : [];
  }

  get languageSelectList() {
    return this.showLanguageSelector ? [this.languageSelectOption] : [];
  }

  get languageSelectOption() {
    return this.createSelectOption(
      'Language',
      this.languages,
      this.selectedLanguageValue,
      this.selectedLanguageText,
      (value) => this.languageChange.emit(value)
    );
  }

  get standardDropdown() {
    return this.createSelectOption(
      'Standard',
      this.methodList,
      [this.selectedMethod],
      this.selectedMethod.name,
      (value) => this.methodologyChange.emit(value as Standard[])
    );
  }

  get pcrDropdown() {
    return this.createSelectOption('PCR', this.pcrList, [this.selectedPcr], this.selectedPcrName, (value) =>
      this.pcrChange.emit(value as PCR[])
    );
  }

  get showLanguageSelector() {
    return (this.languages?.length || 0) > 1;
  }

  get declarationUnitLabel() {
    return this.selectedPcr ? `Declaration unit: ${this.selectedPcr.declared_unit}` : '';
  }

  get selectedPcrName() {
    return this.selectedPcr?.name || '';
  }

  get selectedLanguageValue() {
    return [this.selectedLanguage || {}];
  }

  get selectedLanguageText() {
    return this.selectedLanguage?.text || '';
  }

  private createSelectOption(
    helperText: string,
    options: any[] | null,
    selectedValue: any[],
    text: string,
    change: (value: any[]) => void
  ): HeaderSelectOption {
    return { helperText, options, selectedValue, text, change };
  }
}
